import { s, typographyTokens } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { ChartEmptyState } from '@/ui/composites/charts/ChartEmptyState';
import { Panel } from '@/ui/composites/Panel';
import { Stack } from '@/ui/layout/Stack';
import {
  BarChart,
  Box,
  LineChart,
  LineChartSkeleton,
  StackedBarChartSkeleton,
} from '@/ui/primitives';
import { StackedBarChart, type StackedBarChartDatum } from '@/ui/primitives/StackedBarChart';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import type { UiTone } from '@/theme/uiTokens';
import type { ReactElement } from 'react';

type ChartTab = 'status' | 'pass-rate' | 'frequency' | 'duration';

type StackedSeries = {
  key: string;
  label: string;
  color?: string;
  colorKey?: UiTone;
};

type ExecutionMetricsChartPanelProps = {
  chartTab: ChartTab;
  tabsAriaLabel: string;
  onChartTabChange: (nextTab: ChartTab) => void;
  showChartDateClear: boolean;
  onChartDateClear: () => void;
  chartHeight: number;
  suppressChartAnimation: boolean;
  stackedChartData: { data: StackedBarChartDatum[]; series: StackedSeries[] } | null;
  passRateChartData: Array<Record<string, string | number>> | null;
  frequencyChartData: Array<Record<string, string | number>> | null;
  durationChartData: Array<Record<string, string | number>> | null;
  passRateGuideLines: Array<{ value: number; color: string }>;
  frequencyGuideLines: Array<{ value: number; color: string }>;
  durationGuideLines: Array<{ value: number; color: string }>;
  onChartSelect: (dataIndex: number) => void;
  showLineChartPoints: boolean;
  frequencySeriesLabel?: string;
  emptyStateTitle?: string;
};

const hasVisibleChartValue = (
  data: Array<Record<string, string | number>> | null,
  key: string,
): boolean =>
  Boolean(
    data?.some((row) => {
      const value = row[key];
      return typeof value === 'number' && Number.isFinite(value) && value > 0;
    }),
  );

const hasVisibleStackedChartValue = (
  data: Array<Record<string, string | number>>,
  series: Array<{ key: string }>,
): boolean =>
  data.some((row) =>
    series.some((seriesItem) => {
      const value = row[seriesItem.key];
      return typeof value === 'number' && Number.isFinite(value) && value > 0;
    }),
  );

export const ExecutionMetricsChartPanel = ({
  chartTab,
  tabsAriaLabel,
  onChartTabChange,
  showChartDateClear,
  onChartDateClear,
  chartHeight,
  suppressChartAnimation,
  stackedChartData,
  passRateChartData,
  frequencyChartData,
  durationChartData,
  passRateGuideLines,
  frequencyGuideLines,
  durationGuideLines,
  onChartSelect,
  showLineChartPoints,
  frequencySeriesLabel = 'Executions',
  emptyStateTitle = 'No executions found',
}: ExecutionMetricsChartPanelProps): ReactElement => {
  const hasStatusData = stackedChartData
    ? hasVisibleStackedChartValue(stackedChartData.data, stackedChartData.series)
    : false;
  const hasPassRateData = hasVisibleChartValue(passRateChartData, 'passRate');
  const hasFrequencyData = hasVisibleChartValue(frequencyChartData, 'frequency');
  const hasDurationData = hasVisibleChartValue(durationChartData, 'duration');

  return (
    <Panel padding="md" gap="none" contentGap="lg" sx={{ backgroundColor: 'background.paper' }}>
      <Stack gap="md">
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" align="center" justify="space-between" sx={{ minWidth: 0 }}>
            <Tabs
              value={chartTab}
              onChange={(_event, next) => {
                onChartTabChange(next as ChartTab);
              }}
              aria-label={tabsAriaLabel}
            >
              <Tab label="Status" value="status" />
              <Tab label="Pass Rate" value="pass-rate" />
              <Tab label="Frequency" value="frequency" />
              <Tab label="Duration" value="duration" />
            </Tabs>
            {showChartDateClear ? (
              <Box
                component="button"
                type="button"
                onClick={() => {
                  onChartDateClear();
                }}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontSize: typographyTokens.captionSmall.fontSize,
                  mr: s('md'),
                  background: 'none',
                  border: 'none',
                  padding: s('none'),
                  userSelect: 'none',
                  font: 'inherit',
                  lineHeight: 'inherit',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                RESET
              </Box>
            ) : null}
          </Stack>
        </Box>
        {chartTab === 'status' ? (
          stackedChartData ? (
            hasStatusData ? (
              <StackedBarChart
                data={stackedChartData.data}
                series={stackedChartData.series}
                height={chartHeight}
                skipAnimation={suppressChartAnimation}
                onSelectBar={onChartSelect}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState title={emptyStateTitle} body="Try adjusting filters or search." />
              </Box>
            )
          ) : (
            <StackedBarChartSkeleton height={chartHeight} />
          )
        ) : chartTab === 'pass-rate' ? (
          passRateChartData ? (
            hasPassRateData ? (
              <BarChart
                data={passRateChartData}
                series={[{ key: 'passRate', label: '', color: uiTokens.statusColors.green }]}
                height={chartHeight}
                showLegend={false}
                yReferenceLines={passRateGuideLines}
                onSelectBar={onChartSelect}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState title={emptyStateTitle} body="Try adjusting filters or search." />
              </Box>
            )
          ) : (
            <StackedBarChartSkeleton height={chartHeight} />
          )
        ) : chartTab === 'frequency' ? (
          frequencyChartData ? (
            hasFrequencyData ? (
              <LineChart
                data={frequencyChartData}
                series={[
                  {
                    key: 'frequency',
                    label: frequencySeriesLabel,
                    color: 'var(--color-pink)',
                  },
                ]}
                height={chartHeight}
                showLegend={false}
                yReferenceLines={frequencyGuideLines}
                onSelectBar={onChartSelect}
                showPoints={showLineChartPoints || frequencyChartData.length === 1}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState title={emptyStateTitle} body="Try adjusting filters or search." />
              </Box>
            )
          ) : (
            <LineChartSkeleton height={chartHeight} />
          )
        ) : durationChartData ? (
          hasDurationData ? (
            <LineChart
              data={durationChartData}
              series={[{ key: 'duration', label: '' }]}
              height={chartHeight}
              showLegend={false}
              yReferenceLines={durationGuideLines}
              onSelectBar={onChartSelect}
              showPoints={showLineChartPoints || durationChartData.length === 1}
            />
          ) : (
            <Box
              sx={{
                height: chartHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChartEmptyState title={emptyStateTitle} body="Try adjusting filters or search." />
            </Box>
          )
        ) : (
          <LineChartSkeleton height={chartHeight} />
        )}
      </Stack>
    </Panel>
  );
};
